import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { userInfoService } from '../../api/services';
import { UserInfoState } from '../../models/userInfo';
import Header from '../common/Header';
import Sidebar from '../common/Sidebar';
import { useHub } from '../../context/hub';
import { DEFAULT_SIDEBAR_PROPS } from '../common/Sidebar';
import DriverDrawer from './DriverDrawer';
import UserInfoMapView from './UserInfoMapView';

interface UserInfoViewProps {
  onDepotConfigurationClick?: () => void;
}

const UserInfoView: React.FC<UserInfoViewProps> = ({ onDepotConfigurationClick }) => {
  const { hubId } = useParams<{ hubId: string }>();
  const navigate = useNavigate();
  const { currentHubId, changeHub } = useHub();
  
  // Initialize state based on UserInfoState interface
  const [state, setState] = useState<UserInfoState>({
    userInfo: null,
    loading: true,
    error: null,
    selectedDrivers: [],
    visibleTouchPoints: [],
    showGeoBarriers: true,
    searchTerm: '',
    sortConfig: {
      key: 'name',
      direction: 'asc'
    }
  });
  
  // Effect to fetch user info data when component mounts or hubId changes
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setState(prev => ({ 
          ...prev, 
          loading: true, 
          error: null 
        }));
        
        const id = hubId || currentHubId || 'default';
        console.log('[UserInfoView] Fetching user info for hub:', id);
        const userInfo = await userInfoService.getUserInfo(id);
        
        // Initially set all drivers as selected
        const allDriverIds = userInfo.drivers.map(driver => driver.vehicle.vehicleId);
        
        // Initially set all touch points as visible
        const allTouchPointIds = userInfo.pastTps.map(tp => tp.tpId);
        
        setState(prev => ({
          ...prev,
          userInfo,
          loading: false,
          selectedDrivers: allDriverIds,
          visibleTouchPoints: allTouchPointIds,
        }));
      } catch (error) {
        console.error('Failed to fetch user info:', error);
        setState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load user info data. Please try again later.',
        }));
      }
    };
    
    fetchUserInfo();
  }, [hubId, currentHubId]);
  
  // Handle hub ID changes from URL
  useEffect(() => {
    if (hubId && hubId !== 'default' && hubId !== currentHubId) {
      changeHub(hubId);
    }
  }, [hubId, currentHubId, changeHub]);
  
  // Handle navigation back to task summary
  const handleBackToSummary = () => {
    console.log('[UserInfoView] Navigating back to task summary');
    navigate(`/task-summary/hub/${currentHubId || 'default'}`, { replace: true });
  };
  
  // Handle navigation from the Route Plan button (without full page reload)
  const handleRoutePlanClick = () => {
    console.log('[UserInfoView] Navigating to task summary');
    // Use replace instead of push to avoid adding to history stack
    navigate(`/task-summary/hub/${currentHubId || 'default'}`, { replace: true });
  };

  const handleControlTowerClick = () => {
    navigate('/control-tower', { replace: true });
  };
  
  // Handle re-navigation to the User Info view (refreshes data without full page reload)
  const handleUserInfoClick = () => {
    // Instead of navigating again (which causes remount), just refetch the data
    if (currentHubId) {
      console.log('[UserInfoView] Refreshing data for current hub:', currentHubId);
      
      // Just trigger a data refresh without navigation
      const refreshUserInfo = async () => {
        try {
          setState(prev => ({ 
            ...prev, 
            loading: true, 
            error: null 
          }));
          
          const userInfo = await userInfoService.getUserInfo(currentHubId);
          
          // Update selected drivers and visible touch points
          const allDriverIds = userInfo.drivers.map(driver => driver.vehicle.vehicleId);
          const allTouchPointIds = userInfo.pastTps.map(tp => tp.tpId);
          
          setState(prev => ({
            ...prev,
            userInfo,
            loading: false,
            selectedDrivers: allDriverIds,
            visibleTouchPoints: allTouchPointIds,
          }));
        } catch (error) {
          console.error('Failed to refresh user info:', error);
          setState(prev => ({
            ...prev,
            loading: false,
            error: 'Failed to refresh user info data. Please try again later.',
          }));
        }
      };
      
      refreshUserInfo();
    }
  };
  
  // Handle driver selection
  const handleDriverSelect = (driverId: string) => {
    setState(prev => {
      const selected = [...prev.selectedDrivers];
      const index = selected.indexOf(driverId);
      
      if (index === -1) {
        selected.push(driverId);
      } else {
        selected.splice(index, 1);
      }
      
      return { ...prev, selectedDrivers: selected };
    });
  };
  
  // Handle touch point visibility toggle
  const handleTouchPointVisibilityToggle = (touchPointId: string) => {
    setState(prev => {
      const visible = [...prev.visibleTouchPoints];
      const index = visible.indexOf(touchPointId);
      
      if (index === -1) {
        visible.push(touchPointId);
      } else {
        visible.splice(index, 1);
      }
      
      return { ...prev, visibleTouchPoints: visible };
    });
  };
  
  // Handle geo barrier visibility toggle
  const handleGeoBarrierToggle = () => {
    setState(prev => ({
      ...prev,
      showGeoBarriers: !prev.showGeoBarriers
    }));
  };
  
  // Handle search term change
  const handleSearchChange = (term: string) => {
    setState(prev => ({
      ...prev,
      searchTerm: term
    }));
  };
  
  // Handle sort configuration change
  const handleSortChange = (
    key: 'name' | 'wtCapKg' | 'maxTp' | 'maxRangeKm' | 'vehicleType',
    direction: 'asc' | 'desc'
  ) => {
    setState(prev => ({
      ...prev,
      sortConfig: { key, direction }
    }));
  };
  
  // Effect to handle hub changes and update the URL
  useEffect(() => {
    if (currentHubId && (!hubId || hubId !== currentHubId)) {
      console.log('[UserInfoView] Current hub changed, updating URL:', currentHubId);
      
      // Update URL without triggering a full navigation/remount
      navigate(`/user-info/hub/${currentHubId}`, { replace: true });
    }
  }, [currentHubId, hubId, navigate]);
  
  // Loading state - MODIFIED to keep header and sidebar visible
  if (state.loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', flexDirection: 'column' }}>
        <Header 
          isTaskSummary={true}
          isUserInfo={true}
          showMetrics={false}
          loading={state.loading}
          onBackToSummary={handleBackToSummary}
          taskInfo={null}
        />
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Sidebar 
            {...DEFAULT_SIDEBAR_PROPS} 
            onUserInfoClick={handleUserInfoClick}
            onRoutePlanClick={handleRoutePlanClick}
            onDepotConfigurationClick={onDepotConfigurationClick}
            onControlTowerClick={handleControlTowerClick}
            activeItem="userInfo"
          />
          
          {/* Loading indicator in the content area */}
          <Box sx={{ 
            display: 'flex', 
            flex: 1, 
            justifyContent: 'center', 
            alignItems: 'center'
          }}>
            <CircularProgress />
          </Box>
        </Box>
      </Box>
    );
  }
  
  // Error state - MODIFIED to keep header and sidebar visible
  if (state.error) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', flexDirection: 'column' }}>
        <Header 
          isTaskSummary={true}
          isUserInfo={true}
          showMetrics={false}
          loading={false}
          onBackToSummary={handleBackToSummary}
          taskInfo={null}
        />
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Sidebar 
            {...DEFAULT_SIDEBAR_PROPS} 
            onUserInfoClick={handleUserInfoClick}
            onRoutePlanClick={handleRoutePlanClick}
            onDepotConfigurationClick={onDepotConfigurationClick}
            onControlTowerClick={handleControlTowerClick}
            activeItem="userInfo"
          />
          
          {/* Error display in the content area */}
          <Box sx={{ 
            display: 'flex', 
            flex: 1, 
            justifyContent: 'center', 
            alignItems: 'center',
            flexDirection: 'column'
          }}>
            <Typography variant="h6" color="error" gutterBottom>
              Error
            </Typography>
            <Typography variant="body1">{state.error}</Typography>
          </Box>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', height: '100vh', flexDirection: 'column' }}>
      <Header 
        isTaskSummary={true}
        isUserInfo={true}
        showMetrics={false}
        loading={state.loading}
        onBackToSummary={handleBackToSummary}
        taskInfo={null}
      />
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Sidebar 
          {...DEFAULT_SIDEBAR_PROPS} 
          onUserInfoClick={handleUserInfoClick}
          onRoutePlanClick={handleRoutePlanClick}
          onDepotConfigurationClick={onDepotConfigurationClick}
          onControlTowerClick={handleControlTowerClick}
          activeItem="userInfo"
        />
        
        {/* Main content area with driver drawer and map */}
        <Box sx={{ 
          display: 'flex', 
          flex: 1, 
          overflow: 'hidden',
          position: 'relative'
        }}>
          {/* Driver drawer */}
          <DriverDrawer
            userInfo={state.userInfo}
            selectedDrivers={state.selectedDrivers}
            visibleTouchPoints={state.visibleTouchPoints}
            showGeoBarriers={state.showGeoBarriers}
            searchTerm={state.searchTerm}
            sortConfig={state.sortConfig}
            onDriverSelect={handleDriverSelect}
            onTouchPointVisibilityToggle={handleTouchPointVisibilityToggle}
            onGeoBarrierToggle={handleGeoBarrierToggle}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
          />
          
          {/* Map view takes remaining space */}
          <Box sx={{ 
            flex: 1,
            overflow: 'hidden',
            position: 'relative',
            ml: 2, // Add some margin between drawer and map
            mr: 2,
            my: 2,
            borderRadius: 1,
            boxShadow: 1
          }}>
            <UserInfoMapView
              userInfo={state.userInfo}
              loading={state.loading}
              error={state.error}
              visibleTouchPoints={state.visibleTouchPoints}
              showGeoBarriers={state.showGeoBarriers}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserInfoView;
